import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';

const PrivacyPolicy: React.FC = () => {
    const [markdown, setMarkdown] = useState<string>('');

    useEffect(() => {
        // Fetch the markdown content
        fetch(`${process.env.PUBLIC_URL}/Assets/ScholarCRMPrivacyPolicy.md`)
            .then(response => response.text())
            .then(text => {
                setMarkdown(text);
            })
            .catch(error => console.error('Error loading markdown:', error));
        
        // Add particles.js for background effect
        const script = document.createElement('script');
        script.src = 'https://cdn.jsdelivr.net/particles.js/2.0.0/particles.min.js';
        script.async = true;
        script.onload = () => {
            // @ts-ignore
            if (window.particlesJS) {
                // @ts-ignore
                window.particlesJS('particles-js', {
                    particles: {
                        number: { value: 40, density: { enable: true, value_area: 800 } },
                        color: { value: '#ffffff' },
                        shape: { type: 'circle' },
                        opacity: { value: 0.3, random: false },
                        size: { value: 3, random: true },
                        line_linked: {
                            enable: true,
                            distance: 150,
                            color: '#ffffff',
                            opacity: 0.2,
                            width: 1
                        },
                        move: {
                            enable: true,
                            speed: 1,
                            direction: 'none',
                            random: false,
                            straight: false,
                            out_mode: 'out',
                            bounce: false
                        }
                    },
                    interactivity: {
                        detect_on: 'canvas',
                        events: {
                            onhover: { enable: true, mode: 'grab' },
                            onclick: { enable: false, mode: 'push' },
                            resize: true
                        },
                        modes: {
                            grab: { distance: 140, line_linked: { opacity: 0.3 } }
                        }
                    },
                    retina_detect: true
                });
            }
        };
        document.body.appendChild(script);

        // Add custom CSS for markdown styling
        const style = document.createElement('style');
        style.innerHTML = `
            .markdown-content h1 {
                font-size: 2.25rem;
                font-weight: 800;
                margin-top: 2rem;
                margin-bottom: 1rem;
                color: #4338ca;
            }
            .markdown-content h2 {
                font-size: 1.75rem;
                font-weight: 700;
                margin-top: 1.75rem;
                margin-bottom: 0.75rem;
                color: #5b21b6;
            }
            .markdown-content h3 {
                font-size: 1.5rem;
                font-weight: 600;
                margin-top: 1.5rem;
                margin-bottom: 0.5rem;
                color: #6d28d9;
            }
            .markdown-content h4 {
                font-size: 1.25rem;
                font-weight: 600;
                margin-top: 1.25rem;
                margin-bottom: 0.5rem;
            }
            .markdown-content p {
                margin-bottom: 1rem;
                line-height: 1.7;
            }
            .markdown-content ul, .markdown-content ol {
                margin: 1rem 0 1rem 2rem;
            }
            .markdown-content li {
                margin: 0.5rem 0;
                line-height: 1.6;
            }
            .markdown-content a {
                color: #3b82f6;
                text-decoration: underline;
            }
            .markdown-content blockquote {
                padding-left: 1rem;
                border-left: 4px solid #e5e7eb;
                color: #4b5563;
                font-style: italic;
                margin: 1rem 0;
            }
            .markdown-content hr {
                margin: 2rem 0;
                border: 0;
                height: 1px;
                background: #e5e7eb;
            }
            .markdown-content code {
                background-color: #f3f4f6;
                padding: 0.2rem 0.4rem;
                border-radius: 0.25rem;
                font-family: monospace;
            }
            .markdown-content pre {
                background-color: #1e293b;
                color: #f8fafc;
                padding: 1rem;
                border-radius: 0.5rem;
                overflow-x: auto;
                margin: 1rem 0;
            }
            .markdown-content pre code {
                background-color: transparent;
                padding: 0;
                border-radius: 0;
                color: inherit;
            }
            .markdown-content strong {
                font-weight: 700;
            }
            .markdown-content em {
                font-style: italic;
            }
            .markdown-content table {
                width: 100%;
                border-collapse: collapse;
                margin: 1rem 0;
            }
            .markdown-content th, .markdown-content td {
                padding: 0.75rem;
                border: 1px solid #e5e7eb;
            }
            .markdown-content th {
                background-color: #f9fafb;
                font-weight: 600;
                text-align: left;
            }
        `;
        document.head.appendChild(style);

        return () => {
            if (document.body.contains(script)) {
                document.body.removeChild(script);
            }
            if (document.head.contains(style)) {
                document.head.removeChild(style);
            }
        };
    }, []);

    return (
        <div className="min-h-screen bg-gradient-to-br from-blue-500 via-purple-500 to-indigo-600 flex flex-col">
            {/* Particles background */}
            <div id="particles-js" className="fixed inset-0 z-0"></div>
            
            {/* Header with logo */}
            <div className="relative z-10 flex justify-between items-center p-6 w-full">
                <div className="flex items-center">
                    <img
                        src={`${process.env.PUBLIC_URL}/images/desktop/login-main-logo1.svg`}
                        alt="ScholarCRM Logo"
                        className="h-12"
                    />
                </div>
                <Link 
                    to="/" 
                    className="bg-white/90 hover:bg-white text-blue-800 font-semibold py-2 px-6 rounded-lg shadow-md transition-all hover:shadow-lg flex items-center"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M9.707 14.707a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 1.414L7.414 9H15a1 1 0 110 2H7.414l2.293 2.293a1 1 0 010 1.414z" clipRule="evenodd" />
                    </svg>
                    Back to Home
                </Link>
            </div>
            
            {/* Main content container */}
            <div className="relative z-10 flex-grow flex justify-center p-4 md:p-8">
                <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-2xl w-full max-w-5xl p-6 md:p-10 overflow-auto">
                    <div className="mb-8 text-center">
                        <h1 className="text-3xl md:text-4xl font-extrabold text-blue-800 mb-4">
                            Privacy Policy
                        </h1>
                        <div className="h-1 w-20 bg-blue-500 mx-auto rounded-full"></div>
                    </div>
                    
                    {/* Markdown content with proper styling */}
                    <div className="markdown-content prose prose-blue max-w-none">
                        <ReactMarkdown>
                            {markdown}
                        </ReactMarkdown>
                    </div>
                    
                    {/* Footer with back button */}
                    <div className="mt-10 flex justify-center">
                        <Link 
                            to="/" 
                            className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white py-3 px-8 rounded-xl shadow-md hover:shadow-lg transition-all flex items-center font-semibold"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M9.707 14.707a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 1.414L7.414 9H15a1 1 0 110 2H7.414l2.293 2.293a1 1 0 010 1.414z" clipRule="evenodd" />
                            </svg>
                            Return to Home
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PrivacyPolicy;